import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Calendar, ChevronDown, Eye, Plus, Shuffle, Users, Wallet, X,
} from "lucide-react";
import { useDraftStore } from "@/shared/store/draft";
import { useBloggers, type Blogger } from "@/shared/api/wb";
import { Avatar, Card, PlatformIcon, type Platform } from "@/shared/ui";
import { fmtCpv, fmtNum, fmtRub, fmtRubFull } from "@/shared/lib/format";
import { cn } from "@/shared/lib/cn";
import { StepHead } from "../StepHead";
import { TelegramPost } from "../TelegramPost";
import { WizardLayout } from "../WizardLayout";
import {
  getFormats, PLATFORM_LABELS,
  type CartSelection, type PlatformKey, type FormatType,
} from "@/features/blogger-card";
import type { CreativeVariant } from "@/shared/store/draft";

// ------------------------------------------------------------------
// Main page
// ------------------------------------------------------------------

export function ReviewStep() {
  const {
    draft, branch,
    assignCreative, setBloggerUtm, setPlacementDate,
    removeBloggerFormat, toggleBlogger, updateBloggerFormat,
  } = useDraftStore();
  const navigate = useNavigate();
  const [previewVariantId, setPreviewVariantId] = useState<string | null>(null);
  const [bulkDate, setBulkDate] = useState("");
  const [bulkDateTo, setBulkDateTo] = useState("");
  const [bulkPeriodMode, setBulkPeriodMode] = useState(false);

  const variants = draft.variants ?? [];
  const defaultVariant = variants[0];

  const { data: bloggers } = useBloggers({});
  const selected = (bloggers ?? []).filter((b) => draft.selectedBloggerIds.includes(b.id));

  const totalSum = selected.reduce((s, b) => {
    const fmts = draft.bloggerFormats[b.id] ?? [];
    return s + (fmts.length > 0 ? fmts.reduce((n, f) => n + f.price, 0) : b.price);
  }, 0);
  const totalReach = selected.reduce((s, b) => s + b.reach, 0);
  const cpv = totalReach > 0 ? totalSum / totalReach : 0;
  const cpm = cpv * 1000;

  function distributeEvenly() {
    if (variants.length === 0) return;
    const shuffled = [...variants].sort(() => Math.random() - 0.5);
    selected.forEach((b, i) => assignCreative(b.id, shuffled[i % shuffled.length].id));
  }

  function applyBulkDate() {
    if (!bulkDate) return;
    const dateTo = bulkPeriodMode ? (bulkDateTo || bulkDate) : "";
    selected.forEach((b) => setPlacementDate(b.id, bulkDate, dateTo));
  }

  const isNative = branch.campaignType === "native";
  const missingDatesCount = selected.filter((b) => !draft.placementDates[b.id]?.date).length;

  const previewVariant = previewVariantId
    ? (variants.find((v) => v.id === previewVariantId) ?? null)
    : null;

  return (
    <WizardLayout>
      <StepHead
        step="04"
        title="Обзор"
        sub="Финальная проверка перед запуском. Уточните форматы, смените креатив или удалите лишние каналы — без возврата на предыдущие шаги."
      />

      {/* Metrics */}
      <div className="grid grid-cols-4 gap-3 mt-5 mb-4">
        <Stat icon={<Eye size={14} />} label="Охват" value={totalReach ? fmtNum(totalReach) : "—"} />
        <Stat icon={<Wallet size={14} />} label="Итого" value={fmtRubFull(totalSum)} accent />
        <Stat label="CPV" value={cpv ? fmtCpv(cpv) : "—"} tip="Стоимость одного просмотра" />
        <Stat label="CPM" value={cpm ? fmtRub(Math.round(cpm)) : "—"} tip="Стоимость 1000 просмотров" />
      </div>

      {selected.length === 0 ? (
        <Card className="!p-10 text-center">
          <Users size={28} className="mx-auto mb-3 text-ink-30" />
          <div className="font-display font-semibold text-ink-60 mb-4">Нет каналов — добавьте, чтобы продолжить</div>
          <button
            onClick={() => navigate("/campaign/new/bloggers", { state: { returnTo: "/campaign/new/review" } })}
            className="inline-flex items-center gap-1.5 h-9 px-4 rounded-pill bg-lime text-[var(--fg-on-accent)] font-display font-semibold text-[13px] hover:[background:color-mix(in_srgb,var(--wb-lime)_88%,black)]"
          >
            <Plus size={14} /> Добавить каналы
          </button>
        </Card>
      ) : (
        <>
          {missingDatesCount > 0 && (
            <div className="flex items-center gap-2 px-4 py-2.5 mb-3 rounded-md bg-warning/10 border border-warning/30 text-[12.5px] text-ink">
              <Calendar size={14} className="text-warning flex-shrink-0" />
              Укажите дату размещения ещё для {missingDatesCount} {pluralChannels(missingDatesCount)} — без даты канал не запустится.
            </div>
          )}
          <Card className="!p-0">
            {/* Card toolbar */}
            <div className="flex items-center justify-between px-5 py-3.5 border-b border-ink-12">
              <div className="text-[12.5px] text-ink-40">
                {selected.length} {pluralChannels(selected.length)}
                {defaultVariant && ` · по умолчанию «${defaultVariant.name}»`}
              </div>
              {variants.length > 1 && !isNative && (
                <button
                  onClick={distributeEvenly}
                  className="inline-flex items-center gap-1.5 h-7 px-2.5 rounded-pill border border-ink-12 text-[11.5px] font-display font-semibold text-ink-60 hover:text-ink hover:border-ink/40"
                >
                  <Shuffle size={11} /> Распределить равномерно
                </button>
              )}
            </div>

            {/* Bulk date/period — set once, apply to every selected channel */}
            <div className="flex items-center gap-2.5 flex-wrap px-5 py-2.5 border-b border-ink-12 bg-ink-6/40">
              <span className="inline-flex items-center gap-1.5 text-[11.5px] font-display font-semibold text-ink-60">
                <Calendar size={13} /> Дата для всех каналов
              </span>
              <div className="flex items-center gap-1 p-0.5 rounded-pill bg-white border border-ink-12">
                <button
                  type="button"
                  onClick={() => setBulkPeriodMode(false)}
                  className={cn(
                    "h-6 px-2.5 rounded-pill text-[11px] font-display font-semibold transition-colors",
                    !bulkPeriodMode ? "bg-ink-6 text-ink" : "text-ink-40 hover:text-ink",
                  )}
                >
                  Точная дата
                </button>
                <button
                  type="button"
                  onClick={() => setBulkPeriodMode(true)}
                  className={cn(
                    "h-6 px-2.5 rounded-pill text-[11px] font-display font-semibold transition-colors",
                    bulkPeriodMode ? "bg-ink-6 text-ink" : "text-ink-40 hover:text-ink",
                  )}
                >
                  Период
                </button>
              </div>
              <input
                type="date"
                value={bulkDate}
                onChange={(e) => setBulkDate(e.target.value)}
                className="h-8 px-2.5 rounded-md border border-ink-12 bg-white text-[12px] font-mono outline-none focus:border-ink"
              />
              {bulkPeriodMode && (
                <>
                  <span className="text-ink-40 text-[11px]">—</span>
                  <input
                    type="date"
                    value={bulkDateTo}
                    min={bulkDate || undefined}
                    onChange={(e) => setBulkDateTo(e.target.value)}
                    className="h-8 px-2.5 rounded-md border border-ink-12 bg-white text-[12px] font-mono outline-none focus:border-ink"
                  />
                </>
              )}
              <button
                type="button"
                disabled={!bulkDate}
                onClick={applyBulkDate}
                className="inline-flex items-center gap-1.5 h-8 px-3 rounded-pill bg-ink text-white text-[11.5px] font-display font-semibold hover:bg-[#2A2A2A] disabled:opacity-40 disabled:cursor-not-allowed ml-auto"
              >
                Применить ко всем ({selected.length})
              </button>
            </div>

            {selected.map((b) => (
              <BloggerGroup
                key={b.id}
                blogger={b}
                fmts={draft.bloggerFormats[b.id] ?? []}
                variants={variants}
                assignedVariantId={draft.creativeChannelMap[b.id] ?? defaultVariant?.id ?? ""}
                campaignFormat={branch.campaignType}
                isNative={isNative}
                onPickVariant={(vid) => assignCreative(b.id, vid)}
                onPreviewCreative={(vid) => setPreviewVariantId(vid)}
                onUpdateFormat={(platform, type, entry) =>
                  updateBloggerFormat(b.id, platform as PlatformKey, type as FormatType, entry)
                }
                onRemoveFormat={(platform, type) => removeBloggerFormat(b.id, platform, type)}
                onRemoveBlogger={() => toggleBlogger(b.id)}
                utmContent={draft.utmPerBlogger[b.id] ?? ""}
                defaultLink={defaultVariant?.link ?? ""}
                productName={draft.productName}
                onUtmContentChange={(v) => setBloggerUtm(b.id, v)}
                placementDate={draft.placementDates[b.id]?.date ?? ""}
                placementDateTo={draft.placementDates[b.id]?.date_to ?? ""}
                onPlacementDateChange={(date, dateTo) => setPlacementDate(b.id, date, dateTo)}
              />
            ))}
          </Card>

          <button
            onClick={() => navigate("/campaign/new/bloggers", { state: { returnTo: "/campaign/new/review" } })}
            className="mt-4 inline-flex items-center gap-2 h-9 px-4 rounded-pill border border-dashed border-ink-20 text-[13px] font-display font-semibold text-ink-60 hover:text-ink hover:border-ink/60 transition-colors"
          >
            <Plus size={14} /> Добавить ещё каналы
          </button>
        </>
      )}

      {previewVariant && (
        <CreativePreviewModal
          variant={previewVariant}
          isNative={isNative}
          onClose={() => setPreviewVariantId(null)}
        />
      )}
    </WizardLayout>
  );
}

// ------------------------------------------------------------------
// BloggerGroup — blogger header + one row per CartSelection
// ------------------------------------------------------------------

function slugifyUtm(s: string): string {
  const tr: Record<string, string> = {
    'а':'a','б':'b','в':'v','г':'g','д':'d','е':'e','ё':'e','ж':'zh','з':'z',
    'и':'i','й':'i','к':'k','л':'l','м':'m','н':'n','о':'o','п':'p','р':'r',
    'с':'s','т':'t','у':'u','ф':'f','х':'h','ц':'ts','ч':'ch','ш':'sh',
    'щ':'sch','ъ':'','ы':'y','ь':'','э':'e','ю':'yu','я':'ya',
  };
  return s.toLowerCase().split('').map(c => tr[c] ?? c).join('')
    .replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '').slice(0, 50) || 'campaign';
}

function buildBloggerUtm(platform: string, productName: string, utmContent?: string): string {
  const parts = [
    `utm_source=${encodeURIComponent(platform.toLowerCase())}`,
    `utm_medium=wowblogger`,
    `utm_campaign=${slugifyUtm(productName)}`,
  ];
  if (utmContent?.trim()) parts.push(`utm_content=${encodeURIComponent(utmContent.trim())}`);
  return parts.join('&');
}

type BloggerGroupProps = {
  blogger: Blogger;
  fmts: CartSelection[];
  variants: { id: string; name: string }[];
  assignedVariantId: string;
  campaignFormat: "seeding" | "native";
  isNative: boolean;
  onPickVariant: (vid: string) => void;
  onPreviewCreative: (vid: string) => void;
  onUpdateFormat: (platform: string, type: string, entry: { id: string; name: string; price: number; cpv: number }) => void;
  onRemoveFormat: (platform: PlatformKey, type: FormatType) => void;
  onRemoveBlogger: () => void;
  utmContent: string;
  defaultLink: string;
  productName: string;
  onUtmContentChange: (v: string) => void;
  placementDate: string;
  placementDateTo: string;
  onPlacementDateChange: (date: string, dateTo: string) => void;
};

function BloggerGroup({
  blogger, fmts, variants, assignedVariantId, campaignFormat, isNative,
  onPickVariant, onPreviewCreative, onUpdateFormat,
  onRemoveFormat, onRemoveBlogger,
  utmContent, defaultLink, productName, onUtmContentChange,
  placementDate, placementDateTo, onPlacementDateChange,
}: BloggerGroupProps) {
  const [utmOpen, setUtmOpen] = useState(false);
  const [periodMode, setPeriodMode] = useState(!!placementDateTo);
  // External changes (e.g. "apply to all") can set placementDateTo without
  // this row ever having toggled locally — show the range fields either way.
  const showPeriod = periodMode || !!placementDateTo;
  const autoUtm = buildBloggerUtm(blogger.platform, productName, utmContent);
  const finalUrl = defaultLink
    ? `${defaultLink}${defaultLink.includes('?') ? '&' : '?'}${autoUtm}`
    : '';

  const rows = fmts.length > 0
    ? fmts
    : ([{ type: campaignFormat === "seeding" ? "posev" : "nativ", platform: blogger.platform as PlatformKey, formatId: "", formatName: "—", price: blogger.price, cpv: blogger.cpv, placementDate: "" }] as CartSelection[]);

  return (
    <div className="border-b border-ink-12/60 last:border-b-0">
      {/* Blogger header */}
      <div className="flex items-center gap-3 px-5 py-3 bg-ink-6/50">
        <Avatar name={blogger.full} size={32} grad={blogger.grad as [string, string]} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 font-display font-semibold text-[13.5px]">
            <PlatformIcon platform={blogger.platform as Platform} size={12} />
            {blogger.name}
          </div>
          <div className="text-[11px] text-ink-40 truncate">{blogger.full}</div>
          {blogger.description && (
            <div className="text-[10.5px] text-ink-40 truncate">{blogger.description}</div>
          )}
        </div>
        <div className="text-[11px] text-ink-40 font-mono">~{fmtNum(blogger.reach)} просм.</div>
        <button
          onClick={onRemoveBlogger}
          title="Убрать блогера"
          className="inline-flex items-center gap-1 h-7 px-2.5 rounded-pill border border-ink-12 text-[11.5px] font-display font-semibold text-ink-40 hover:text-pink hover:border-pink/40 transition-colors"
        >
          <X size={11} strokeWidth={2.5} /> Убрать
        </button>
      </div>

      {/* Placement date/period — required, sent to the blogger with the creative */}
      <div className="flex items-center gap-2.5 flex-wrap px-5 py-2.5 border-t border-ink-12/40 bg-white">
        <span className="inline-flex items-center gap-1.5 text-[11.5px] font-display font-semibold text-ink-60">
          <Calendar size={13} className={cn(!placementDate && "text-warning")} /> Дата размещения
        </span>
        <div className="flex items-center gap-1 p-0.5 rounded-pill bg-ink-6">
          <button
            type="button"
            onClick={() => { setPeriodMode(false); onPlacementDateChange(placementDate, ""); }}
            className={cn(
              "h-6 px-2.5 rounded-pill text-[11px] font-display font-semibold transition-colors",
              !showPeriod ? "bg-white shadow-sm text-ink" : "text-ink-40 hover:text-ink",
            )}
          >
            Точная дата
          </button>
          <button
            type="button"
            onClick={() => { setPeriodMode(true); onPlacementDateChange(placementDate, placementDateTo || placementDate); }}
            className={cn(
              "h-6 px-2.5 rounded-pill text-[11px] font-display font-semibold transition-colors",
              showPeriod ? "bg-white shadow-sm text-ink" : "text-ink-40 hover:text-ink",
            )}
          >
            Период
          </button>
        </div>
        <input
          type="date"
          required
          value={placementDate}
          onChange={(e) => onPlacementDateChange(e.target.value, showPeriod ? (placementDateTo || e.target.value) : "")}
          className={cn(
            "h-8 px-2.5 rounded-md border bg-white text-[12px] font-mono outline-none focus:border-ink",
            placementDate ? "border-ink-12" : "border-warning",
          )}
        />
        {showPeriod && (
          <>
            <span className="text-ink-40 text-[11px]">—</span>
            <input
              type="date"
              required
              min={placementDate || undefined}
              value={placementDateTo}
              onChange={(e) => onPlacementDateChange(placementDate, e.target.value)}
              className="h-8 px-2.5 rounded-md border border-ink-12 bg-white text-[12px] font-mono outline-none focus:border-ink"
            />
          </>
        )}
        {!placementDate && (
          <span className="text-[11px] text-warning font-display font-semibold">Без даты канал не запустится</span>
        )}
      </div>

      {/* Format rows */}
      {rows.map((fmt) => (
        <FormatRow
          key={`${fmt.platform}::${fmt.type}`}
          fmt={fmt}
          variants={variants}
          assignedVariantId={assignedVariantId}
          hasFmtData={fmts.length > 0}
          onPickVariant={onPickVariant}
          onPreviewCreative={onPreviewCreative}
          onUpdateFormat={onUpdateFormat}
          onRemoveFormat={fmts.length > 0 ? onRemoveFormat : undefined}
        />
      ))}

      {/* UTM section */}
      <>
        <button
          onClick={() => setUtmOpen((o) => !o)}
          className="w-full flex items-center justify-between px-5 py-1.5 text-[11.5px] text-ink-40 hover:text-ink hover:bg-ink-6"
        >
          <span className="inline-flex items-center gap-1.5 font-mono">
            UTM-метки
            <span className="text-ink-30">
              utm_source=<b className="text-ink-60">{blogger.platform}</b>
              &nbsp;&middot;&nbsp;medium=<b className="text-ink-60">wowblogger</b>
            </span>
          </span>
          <ChevronDown size={12} className={cn("transition-transform", utmOpen && "rotate-180")} />
        </button>
        {utmOpen && (
          <div className="px-5 pb-3 pt-2 grid gap-2 fade-up bg-ink-6/30">
            {/* Auto-generated UTM (read-only) */}
            <div className="grid gap-1">
              <div className="text-[10px] font-display font-bold uppercase tracking-[0.07em] text-ink-40">
                Авто-UTM для этого канала
              </div>
              <div className="h-8 px-2.5 flex items-center rounded-sm border border-ink-12 bg-white text-[11.5px] font-mono text-ink-60 overflow-hidden">
                <span className="truncate">{autoUtm}</span>
              </div>
            </div>

            {/* utm_content — optional, toggle */}
            <div className="grid gap-1">
              <label className="flex items-center gap-2 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={!!utmContent}
                  onChange={(e) => onUtmContentChange(e.target.checked ? blogger.name.replace('@', '') : "")}
                  className="w-3.5 h-3.5 rounded accent-[var(--wb-lime)] cursor-pointer"
                />
                <span className="text-[10px] font-display font-bold uppercase tracking-[0.07em] text-ink-40">
                  utm_content — уточнение по каналу
                </span>
              </label>
              {!!utmContent && (
                <input
                  type="text"
                  placeholder={`${blogger.name.replace('@', '')}`}
                  value={utmContent}
                  onChange={(e) => onUtmContentChange(e.target.value)}
                  className="h-8 px-2.5 rounded-sm border border-ink-12 bg-white text-[12px] font-mono outline-none focus:border-ink"
                />
              )}
            </div>

            {/* Final URL preview */}
            {finalUrl && (
              <div className="text-[10.5px] text-ink-40 font-mono break-all" title={finalUrl}>
                → {finalUrl}
              </div>
            )}
          </div>
        )}
      </>
    </div>
  );
}

// ------------------------------------------------------------------
// FormatRow — single placement line
// ------------------------------------------------------------------

type FormatRowProps = {
  fmt: CartSelection;
  variants: { id: string; name: string }[];
  assignedVariantId: string;
  hasFmtData: boolean;
  onPickVariant: (vid: string) => void;
  onPreviewCreative: (vid: string) => void;
  onUpdateFormat: (platform: string, type: string, entry: { id: string; name: string; price: number; cpv: number }) => void;
  onRemoveFormat?: (platform: PlatformKey, type: FormatType) => void;
};

function FormatRow({
  fmt, variants, assignedVariantId, hasFmtData,
  onPickVariant, onPreviewCreative, onUpdateFormat, onRemoveFormat,
}: FormatRowProps) {
  const availableFormats = getFormats(fmt.platform as PlatformKey, fmt.type as FormatType);
  const platformLabel = PLATFORM_LABELS[fmt.platform as PlatformKey] ?? fmt.platform.toUpperCase();
  const isPosev = fmt.type === "posev";
  const typeColors = isPosev
    ? { bg: "bg-lime/20", text: "text-ink" }
    : { bg: "bg-[#E6F1FB]", text: "text-[#185FA5]" };

  return (
    <div className="grid grid-cols-[auto_1fr_100px_1fr_36px] gap-3 items-center px-5 py-2.5 border-t border-ink-12/40 bg-white">
      {/* Type + platform badge */}
      <div className="flex items-center gap-1.5">
        <PlatformIcon platform={fmt.platform as Platform} size={13} />
        <span className={cn(
          "inline-flex items-center h-5 px-2 rounded-pill text-[10.5px] font-display font-bold",
          typeColors.bg, typeColors.text,
        )}>
          {isPosev ? "Посев" : "Натив"} · {platformLabel}
        </span>
      </div>

      {/* Format selector */}
      {hasFmtData && availableFormats.length > 1 ? (
        <select
          value={fmt.formatId}
          onChange={(e) => {
            const entry = availableFormats.find((f) => f.id === e.target.value);
            if (entry) onUpdateFormat(fmt.platform, fmt.type, entry);
          }}
          className="h-8 px-2.5 rounded-md border border-ink-12 bg-white text-[12.5px] font-display font-semibold outline-none focus:border-ink"
        >
          {availableFormats.map((f) => (
            <option key={f.id} value={f.id}>
              {f.name}{f.desc ? ` · ${f.desc}` : ""}
            </option>
          ))}
        </select>
      ) : (
        <div className="text-[12.5px] font-display font-semibold text-ink-60">
          {fmt.formatName || "—"}
        </div>
      )}

      {/* Price */}
      <div className="font-mono font-bold text-[13px] tabular-nums">{fmtRub(fmt.price)}</div>

      {/* Creative selector + preview */}
      <div className="flex items-center gap-1.5">
        {variants.length > 1 ? (
          <select
            value={assignedVariantId}
            onChange={(e) => onPickVariant(e.target.value)}
            className="flex-1 h-8 px-2.5 rounded-md border border-ink-12 bg-white text-[12.5px] font-display font-semibold outline-none focus:border-ink"
          >
            {variants.map((v) => (
              <option key={v.id} value={v.id}>{v.name}</option>
            ))}
          </select>
        ) : (
          <div className="flex-1 text-[12.5px] font-display font-semibold text-ink-60 truncate">
            {variants[0]?.name ?? "—"}
          </div>
        )}
        {assignedVariantId && (
          <button
            onClick={() => onPreviewCreative(assignedVariantId)}
            title={fmt.type === "nativ" ? "Посмотреть ТЗ" : "Посмотреть креатив"}
            className="w-8 h-8 grid place-items-center rounded-md border border-ink-12 text-ink-40 hover:text-ink hover:border-ink/40 flex-shrink-0 transition-colors"
          >
            <Eye size={14} />
          </button>
        )}
      </div>

      {/* Delete format row */}
      {onRemoveFormat ? (
        <button
          onClick={() => onRemoveFormat(fmt.platform as PlatformKey, fmt.type as FormatType)}
          title="Убрать это размещение"
          className="w-8 h-8 grid place-items-center rounded-md text-ink-30 hover:text-pink hover:bg-pink/10 transition-colors"
        >
          <X size={14} />
        </button>
      ) : (
        <div />
      )}
    </div>
  );
}

// ------------------------------------------------------------------
// Creative preview modal
// ------------------------------------------------------------------

function CreativePreviewModal({
  variant, isNative, onClose,
}: {
  variant: CreativeVariant; isNative: boolean; onClose: () => void;
}) {
  return (
    <div
      className="fixed inset-0 z-[110] grid place-items-center p-6"
      style={{ background: "rgba(28,28,28,0.55)", backdropFilter: "blur(6px)" }}
      onClick={onClose}
    >
      <div
        className="bg-[var(--bg)] rounded-lg shadow-lg fade-up flex flex-col overflow-hidden"
        style={{ width: "min(560px, 96vw)", height: "min(85vh, 780px)" }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-ink-12 flex-shrink-0">
          <div>
            <div className="text-[10.5px] font-display font-semibold uppercase tracking-[0.14em] text-ink-40 mb-0.5">
              {isNative ? "ТЗ блогеру" : "Превью · Telegram"}
            </div>
            <div className="font-display font-bold text-[16px] tracking-tight">{variant.name}</div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 grid place-items-center rounded-md hover:bg-ink-6 text-ink-40 hover:text-ink"
          >
            <X size={16} />
          </button>
        </div>

        {/* Body */}
        <div className="overflow-y-auto flex-1 min-h-0 p-5 grid gap-4 content-start">
          {isNative ? (
            <NativeBriefPreview variant={variant} />
          ) : (
            <>
              <TelegramPost p={{ name: variant.name, text: variant.text, media: variant.media, link: variant.link, erid: variant.erid, eridOnImage: variant.eridOnImage }} />
              {variant.link && (
                <div className="px-3 py-2 rounded-md bg-ink-6 border border-ink-12">
                  <div className="text-[9.5px] font-display font-bold uppercase tracking-[0.10em] text-ink-40 mb-0.5">
                    Ссылка в посте
                  </div>
                  <div className="text-[11px] font-mono text-ink-60 break-all">{variant.link}</div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3.5 border-t border-ink-12 flex-shrink-0 flex justify-end">
          <button
            onClick={onClose}
            className="inline-flex items-center gap-1.5 h-9 px-5 rounded-pill bg-ink text-white font-display font-semibold text-[13px] hover:bg-[#333]"
          >
            Закрыть
          </button>
        </div>
      </div>
    </div>
  );
}

function NativeBriefPreview({ variant }: { variant: CreativeVariant }) {
  const sections = [
    { label: "Задача / о чём пост", value: variant.text },
    { label: "Тон и стиль", value: variant.tone },
    { label: "Что нельзя упоминать", value: variant.forbidden ?? "" },
  ].filter((s) => s.value.trim());

  return (
    <div className="grid gap-4">
      <div className="p-3.5 rounded-md bg-[#E6F1FB] text-[12.5px] text-[#185FA5] leading-relaxed font-display font-semibold">
        Блогер получит это ТЗ и напишет пост от себя
      </div>
      {sections.length === 0 ? (
        <div className="p-6 rounded-md border border-dashed border-ink-12 text-center text-ink-40 text-[13px]">
          Бриф ещё не заполнен
        </div>
      ) : (
        sections.map((s) => (
          <div key={s.label} className="grid gap-1.5">
            <div className="text-[10.5px] font-display font-bold uppercase tracking-[0.08em] text-ink-40">
              {s.label}
            </div>
            <div className="p-3.5 rounded-md bg-white border border-ink-12 text-[13.5px] leading-relaxed whitespace-pre-wrap">
              {s.value}
            </div>
          </div>
        ))
      )}
      {variant.media.length > 0 && (
        <div className="grid gap-2">
          <div className="text-[10.5px] font-display font-bold uppercase tracking-[0.08em] text-ink-40">
            Материалы для блогера
          </div>
          <div className="flex gap-2 flex-wrap">
            {variant.media.map((m) => (
              <div key={m.id} className="w-16 h-16 rounded-md overflow-hidden bg-ink-6 flex-shrink-0">
                {m.kind === "video" ? (
                  <div className="grid place-items-center w-full h-full text-ink-40 text-[10px] font-display font-semibold">▶ видео</div>
                ) : (
                  <img src={m.url} alt="" className="w-full h-full object-cover" />
                )}
              </div>
            ))}
          </div>
        </div>
      )}
      {variant.link && (
        <div className="grid gap-1 p-4 rounded-md border border-ink-12 bg-white">
          <div className="text-[10.5px] font-display font-bold uppercase tracking-[0.08em] text-ink-40">
            Ссылка
          </div>
          <div className="text-[12px] font-mono text-ink break-all">{variant.link}</div>
        </div>
      )}
      <div className="p-3.5 rounded-md bg-ink-6 text-[12px] text-ink-40 leading-relaxed">
        Итоговый пост согласуется с вами до публикации.
      </div>
    </div>
  );
}

// ------------------------------------------------------------------
// Helpers
// ------------------------------------------------------------------

function Stat({
  icon, label, value, accent, tip,
}: {
  icon?: React.ReactNode;
  label: string;
  value: string;
  accent?: boolean;
  tip?: string;
}) {
  return (
    <Card className="!p-3.5" title={tip}>
      <div className="flex items-center gap-1.5 text-[10.5px] uppercase tracking-wider text-ink-40 font-display font-semibold mb-1">
        {icon} {label}
      </div>
      <div className={cn("font-display font-extrabold text-[20px] tabular-nums", accent && "text-success")}>
        {value}
      </div>
    </Card>
  );
}

function pluralChannels(n: number): string {
  if (n % 10 === 1 && n % 100 !== 11) return "канал";
  if ([2, 3, 4].includes(n % 10) && ![12, 13, 14].includes(n % 100)) return "канала";
  return "каналов";
}
